import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Modal from "../../components/Modal";
import Alert from "../../components/Alert";
import {
  listTeacherSections,
  createTeacherSection,
  updateTeacherSection,
  deleteTeacherSection,
} from "../../api/teacherSectionApi";
import "../Users/UsersPage.css";

const emptyForm = { name: "", serial: 0 };

/**
 * Its own page rather than a `LookupSettingsPage` config entry — the
 * `core_setting_teacher_section` table has no `status` column, unlike every
 * other lookup resource (see backend `CoreSettingTeacherSection` javadoc).
 */
export default function TeacherSectionsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    listTeacherSections()
      .then(setItems)
      .catch(() => setError("Failed to load teacher sections."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openCreate = () => {
    setForm(emptyForm);
    setModalMode("create");
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({ name: item.name, serial: item.serial });
    setModalMode("edit");
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = { name: form.name, serial: Number(form.serial) };
    try {
      if (modalMode === "create") {
        await createTeacherSection(payload);
        setSuccess("Teacher section created.");
      } else {
        await updateTeacherSection(editingId, payload);
        setSuccess("Teacher section updated.");
      }
      closeModal();
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) {
      return;
    }
    setError("");
    try {
      await deleteTeacherSection(item.id);
      setSuccess("Teacher section deleted.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "name", label: "Name" },
    { key: "serial", label: "Serial" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Teacher Sections</h1>
        <button type="button" className="btn-primary" onClick={openCreate}>
          + Add Teacher Section
        </button>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={items}
          actions={(item) => (
            <>
              <button type="button" className="btn-link" onClick={() => openEdit(item)}>
                Edit
              </button>
              <button type="button" className="btn-link danger" onClick={() => handleDelete(item)}>
                Delete
              </button>
            </>
          )}
        />
      )}

      {modalMode && (
        <Modal title={modalMode === "create" ? "Add Teacher Section" : "Edit Teacher Section"} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="user-form">
            <label>Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

            <label>Serial</label>
            <input
              type="number"
              value={form.serial}
              onChange={(e) => setForm({ ...form, serial: e.target.value })}
              required
            />

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={closeModal}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </MainLayout>
  );
}
